import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import dotty from 'dotty'
import { Field } from 'formik'

import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'

import AddIcon from '@material-ui/icons/Add'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'

import SimpleTable from 'components/table/SimpleTable'
import SimpleTableDeleteDialog from 'components/table/SimpleTableDeleteDialog'
import SimpleTableHeader from 'components/table/SimpleTableHeader'
import SimpleTableActions from 'components/table/SimpleTableActions'

import SortableListItem from './SortableListItem'

import ListDialog from './ListDialog'

import utils from './utils'

const useStyles = makeStyles(theme => ({
  button: {
    marginRight: theme.spacing(2),
  },
  container: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: '1px dotted #e5e5e5',
    paddingBottom: theme.spacing(2),
  },
  titleTypography: {
    color: theme.palette.text.secondary,
  },
  tableData: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  draggable: {
    paddingLeft: '0px',
  }
}))

const FormList = ({
  item,
  formProps,
  storeActions,
  arrayHelpers,
}) => {
  const classes = useStyles()

  const [ editOpen, setEditOpen ] = useState(false)
  const [ editItem, setEditItem ] = useState(null)
  const [ deleteConfirmOpen, setDeleteConfirmOpen ] = useState(false)
  const [ deleteConfirmItem, setDeleteConfirmItem ] = useState(null)

  const baseItem = item
  const value = dotty.get(formProps.values, item.id) || []
  const fields = item.list.table

  const allErrors = dotty.get(formProps.errors, item.id) || []
  const allTouched = dotty.get(formProps.touched, item.id) || []

  const onAdd = () => {
    setEditOpen(true)
    setEditItem(null)
  }

  const onEdit = (item) => {
    setEditOpen(true)
    setEditItem(item)
  }

  const onCancel = () => {
    setEditOpen(false)
    setEditItem(null)
  }

  const onDelete = (id) => {
    arrayHelpers.remove(id)
    onCloseDeleteDialog()
  }

  const onSave = (values) => {
    if(editItem) {
      arrayHelpers.replace(editItem.id, values)
    }
    else {
      const useValues = item.list.onAdd ?
        item.list.onAdd(values) :
        values
      arrayHelpers.push(useValues)
    }
    onCancel()
  }

  const onSwap = (fromIndex, toIndex) => {
    arrayHelpers.swap(fromIndex, toIndex)
  }

  const onOpenDeleteDialog = (item) => {
    setDeleteConfirmOpen(true)
    setDeleteConfirmItem(item)
  }

  const onCloseDeleteDialog = (item) => {
    setDeleteConfirmOpen(false)
    setDeleteConfirmItem(null)
  }
   
  const data = value.map((listItem, index) => {
    const ret = fields.reduce((all, field) => {

      let baseRenderValue = dotty.get(listItem, field.name)

      const editor = field.editor ?
        field.editor(listItem, formProps.values) :
        null

      if(editor) {
        const error = dotty.get(allErrors[index], editor.id)
        const touched = dotty.get(allTouched[index], editor.id)

        baseRenderValue = (
          <Field
            name={ `${item.id}[${index}].${editor.id}` }
            component={ utils.getComponent(editor.component) }
            item={ editor }
            error={ error }
            touched={ touched }
          />
        )
      }
      else if(field.render) {
        baseRenderValue = field.render(listItem, formProps.values)
      }
      
      let renderValue = baseRenderValue

      if(field.sortable) {
        renderValue = (
          <SortableListItem
            index={ index }
            onSwap={ onSwap }
          >
            { baseRenderValue }
          </SortableListItem>
        )
      }
      all[field.name] = renderValue
      return all
    }, {})
    ret.id = index
    ret.index = index
    ret._item = Object.assign({}, listItem, {
      id: index,
      index: index,
    })
    return ret
  })

  const extraData = item.list.getExtraData ? 
    item.list.getExtraData({
      data: value,
      formValues: formProps.values,
    }) : []

  const allData = data.concat(extraData).sort((a, b) => {
    if(!item.list.sortBy) return 0
    const valueA = (a[item.list.sortBy] || '').toLowerCase()
    const valueB = (b[item.list.sortBy] || '').toLowerCase()
    if(valueA > valueB) return 1
    else if(valueA < valueB) return -1
    else return 0
  })

  const addButton = (
    <Button 
      className={ classes.button } 
      variant="contained"
      onClick={ onAdd }
      size="small"
    >
      Add 
      <AddIcon />
    </Button>
  )

  const getActions = (item) => {
    const actions = []
    const itemActionHandlers = baseItem.list.itemActionHandlers
  
    if(!baseItem.list.hideDelete && !item.hideDelete) {
      actions.push({
        title: 'Delete',
        icon: DeleteIcon,
        handler: (data) => {
          if(itemActionHandlers && itemActionHandlers.delete) {
            itemActionHandlers.delete(data._item, storeActions)
          }
          else {
            onOpenDeleteDialog(data._item)
          }
          
        },
      })
    }

    if(!baseItem.list.hideEdit && !item.hideEdit) {
      actions.push({
        title: 'Edit',
        icon: EditIcon,
        handler: (data) => {
          if(itemActionHandlers && itemActionHandlers.edit) {
            itemActionHandlers.edit(data._item, storeActions)
          }
          else {
            onEdit(data._item)
          }
        },
      })
    }

    return (
      <SimpleTableActions
        item={ item }
        actions={ actions }
      />
    )
  }

  const editWindow = editOpen ? (
    item.list.getDialog ? 
      item.list.getDialog({
        open: editOpen,
        onCancel,
        onSave,
        values: formProps.values,
      }) : (
        <ListDialog
          title={ item.title || item.id }
          schema={ item.list.schema }
          open={ editOpen }
          initialValues={ editItem || {} }
          onCancel={ onCancel }
          onSave={ onSave }
          size={ item.list.dialogSize }
        />
      )
  ) : null

  const deleteWindow = deleteConfirmOpen ? (
    <SimpleTableDeleteDialog
      open
      title={ deleteConfirmItem ? dotty.get(deleteConfirmItem, item.list.mainField) : null }
      onCancel={ onCloseDeleteDialog }
      onConfirm={ () => {
        onDelete(deleteConfirmItem.id)
        onCloseDeleteDialog
      }}
    />
  ) : null

  return (
    <div className={ classes.container }>
      <SimpleTableHeader
        getActions={ () => addButton }
        getTitle={ () => (
          <React.Fragment>
            <Typography className={ classes.titleTypography } variant='subtitle1'>{ item.title || item.id }</Typography>
            <Typography className={ classes.titleTypography } variant='caption'>{ item.helperText }</Typography>
          </React.Fragment>
        )}
      />
      <div className={ classes.tableData }>
        <SimpleTable
          data={ allData }
          fields={ fields }
          getActions={ getActions }
          compact={ item.list.compact }
          equalSpacing={ item.list.equalSpacing }
          hideHeaderIfEmpty
          disableLastBorder
        />
      </div>
      { editWindow }
      { deleteWindow }
    </div>
  )

}

export default FormList
